import React from 'react';
import {
  Content,
  Header,
  HeaderLabel,
  Page,
} from '@backstage/core-components';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import HistoryIcon from '@material-ui/icons/History';
import {
  BarChart,
  Bar,
  ComposedChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';

/* ─── Types ─── */

interface CostStatus {
  billingAccountId: string | null;
  bigqueryConfigured: boolean;
  bigqueryTable: string | null;
  projectId: string;
}

interface MonthlySummary {
  month: string;
  totalCost: number;
  totalCredit: number;
  netCost: number;
  tax: number;
  invoiceTotal: number;
  currency: string;
}

interface ServiceCost {
  serviceName: string;
  serviceId: string;
  cost: number;
  credit: number;
  netCost: number;
  currency: string;
}

interface DailyCost {
  date: string;
  totalCost: number;
  totalCredit: number;
  netCost: number;
}

interface SavingsSummaryMonth {
  month: string;
  grossCost: number;
  creditsTotal: number;
  netCost: number;
  savingsPct: number;
  byType: Record<string, number>;
  commitment: {
    fee: number;
    feeOffset: number;
    utilizationPct: number | null;
  };
  eligibleProxyGross: number;
  currency: string;
}

interface SavingsSummary {
  startMonth: string;
  commitment: { sku: string | null; since: string | null };
  monthly: SavingsSummaryMonth[];
}

interface SavingsPotential {
  savingsKRW: number;
  baseline30dKRW: number;
  percentage: number;
  severity: 'low' | 'medium' | 'high';
  breakdown: {
    recommender: number;
    anomaly: number;
  };
  count: number;
}

interface Recommendation {
  source: 'recommender' | 'anomaly';
  category: string;
  resource: string;
  action: string;
  savingsKRW: number;
  priority: 'P1' | 'P2' | 'P3' | 'P4';
}

interface DeptSummary {
  dept: string;
  rawCost: number;
  netCost: number;
  devCost: number;
  prdCost: number;
  namespaces: Array<{ namespace: string; rawCost: number; env: 'dev' | 'prd' }>;
}

interface DeptInsights {
  windowDays: number;
  grandTotalKRW: number;
  departments: DeptSummary[];
  platform: {
    rawCost: number;
    namespaces: Array<{ namespace: string; rawCost: number }>;
  };
}

interface LbConsolidation {
  windowDays: number;
  totalRawCostKRW: number;
  rulesTotal: number;
  rulesK8s: number;
  rulesSystem: number;
  targetRules: number;
  reducibleRules: number;
  estimatedMonthlySavingsKRW: number;
  skuBreakdown: Array<{ sku: string; rawCost: number }>;
  note: string;
}

interface PolicyViolation {
  rule: 'R1-dev-backup' | 'R2-dev-scaledown' | 'R3-orphan-pvc';
  severity: 'high' | 'medium' | 'low';
  resource: string;
  cluster: 'system' | 'service01';
  detail: string;
  estimatedMonthlyWasteKRW: number;
}

interface PolicyViolations {
  count: number;
  totalEstimatedMonthlyWasteKRW: number;
  bySeverity: { high: number; medium: number; low: number };
  violations: PolicyViolation[];
}

interface CatalogEntityCost {
  entityId: string;
  dept: string | null;
  rawCost: number;
  environments: string[];
  workloads: Array<{
    workload: string;
    namespace: string;
    rawCost: number;
    env: 'dev' | 'prd';
  }>;
}

interface CatalogEntityInsights {
  windowDays: number;
  count: number;
  totalRawCostKRW: number;
  entities: CatalogEntityCost[];
}

interface WorkloadAnomaly {
  entityId: string | null;
  workload: string;
  namespace: string;
  recentAvg7d: number;
  baselineAvg30d: number;
  growthRate: number;
  monthlyDeltaKRW: number;
}

interface WorkloadAnomalies {
  windowRecent: number;
  windowBaseline: number;
  count: number;
  totalMonthlyDeltaKRW: number;
  anomalies: WorkloadAnomaly[];
}

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  statsRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  statCard: {
    flex: '1 1 200px',
    minWidth: 180,
  },
  statValue: {
    fontSize: '1.6rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    marginTop: theme.spacing(0.5),
  },
  statChange: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5),
  },
  positive: { color: theme.palette.error.main },
  negative: { color: theme.palette.success.main },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
  tableHeader: {
    fontWeight: 600,
    backgroundColor: theme.palette.background.default,
  },
  costBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main,
  },
  costBarBg: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
    width: '100%',
  },
  setupCard: {
    padding: theme.spacing(3),
    textAlign: 'center',
    marginTop: theme.spacing(2),
  },
  codeBlock: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: 4,
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    marginTop: theme.spacing(1),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
}));

/* ─── Helpers ─── */

const COLORS = [
  '#1976d2', '#f57c00', '#388e3c', '#d32f2f', '#7b1fa2',
  '#0097a7', '#5d4037', '#455a64', '#c2185b', '#00796b',
  '#ff5722', '#3f51b5', '#8bc34a', '#ff9800', '#9c27b0',
];

function formatKRW(value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    return `₩${(value / 10_000).toFixed(0)}만`;
  }
  if (Math.abs(value) >= 10_000) {
    return `₩${(value / 10_000).toFixed(1)}만`;
  }
  return `₩${Math.round(value).toLocaleString()}`;
}

function formatMonth(yyyymm: string): string {
  if (yyyymm.length === 6) {
    return `${yyyymm.slice(0, 4)}-${yyyymm.slice(4)}`;
  }
  return yyyymm;
}

function monthLabel(yyyymm: string): string {
  if (yyyymm.length === 6) {
    return `${yyyymm.slice(4)}월`;
  }
  return yyyymm;
}

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/* ─── Setup Guide Component ─── */

function SetupGuide({
  status,
  classes,
}: {
  status: CostStatus;
  classes: ReturnType<typeof useStyles>;
}) {
  return (
    <Card className={classes.setupCard}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          GCP Cost 대시보드 설정
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          비용 데이터를 조회하려면 아래 설정이 필요합니다.
        </Typography>

        <Box textAlign="left" maxWidth={700} mx="auto">
          <Typography variant="h6" className={classes.sectionTitle}>
            1. BigQuery Billing Export 활성화
          </Typography>
          <Typography variant="body2" color="textSecondary">
            GCP 콘솔 → Billing → Billing export → BigQuery export
          </Typography>
          <Box className={classes.codeBlock}>
            {`프로젝트: ${status.projectId}
데이터셋: billing_export (새로 생성)
유형: Standard usage cost (무료)
※ 데이터 축적에 1~2일 소요`}
          </Box>

          <Typography variant="h6" className={classes.sectionTitle}>
            2. SA 빌링 권한 설정
          </Typography>
          <Box className={classes.codeBlock}>
            {`gcloud billing accounts add-iam-policy-binding ${status.billingAccountId || '<BILLING_ACCOUNT_ID>'} \\
  --member="serviceAccount:arcus-billing-viewer@${status.projectId}.iam.gserviceaccount.com" \\
  --role="roles/billing.viewer"`}
          </Box>

          <Typography variant="h6" className={classes.sectionTitle}>
            3. Helm values 설정
          </Typography>
          <Box className={classes.codeBlock}>
            {`# charts/arcus/values-{prd,dev}.yaml
arcus:
  gcpCost:
    billingAccountId: "${status.billingAccountId || '<BILLING_ACCOUNT_ID>'}"
    bigqueryDataset: "billing_export"`}
          </Box>

          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              현재 상태:&nbsp;
              <Chip
                label={`빌링 계정: ${status.billingAccountId ? '설정됨' : '미설정'}`}
                color={status.billingAccountId ? 'primary' : 'default'}
                size="small"
                style={{ marginRight: 8 }}
              />
              <Chip
                label={`BigQuery: ${status.bigqueryConfigured ? '설정됨' : '미설정'}`}
                color={status.bigqueryConfigured ? 'primary' : 'default'}
                size="small"
              />
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ─── Savings Potential Card ─── */

function SavingsPotentialCard({
  savings,
  onClick,
}: {
  savings: SavingsPotential;
  onClick?: () => void;
}) {
  const color =
    savings.severity === 'high'
      ? '#d32f2f'
      : savings.severity === 'medium'
        ? '#f57c00'
        : '#388e3c';
  const label =
    savings.severity === 'high'
      ? '높음'
      : savings.severity === 'medium'
        ? '주의'
        : '양호';

  return (
    <Card
      variant="outlined"
      style={{
        flex: '2 1 440px',
        minWidth: 360,
        borderLeft: `6px solid ${color}`,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" alignItems="baseline" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Typography
            style={{ fontSize: '2rem', fontWeight: 700, color, lineHeight: 1.1 }}
          >
            {savings.percentage.toFixed(1)}%
          </Typography>
          <Typography style={{ fontSize: '1.1rem', fontWeight: 600 }}>
            {formatKRW(savings.savingsKRW)} / 월
          </Typography>
          <Chip
            label={label}
            size="small"
            style={{ backgroundColor: color, color: '#fff' }}
          />
        </Box>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
          아낄 수 있는 비용 (최근 30일 기준 ₩{Math.round(savings.baseline30dKRW).toLocaleString()})
        </Typography>
        <Box display="flex" mt={1} style={{ gap: 16 }}>
          <Typography variant="caption" color="textSecondary">
            Recommender: {formatKRW(savings.breakdown.recommender)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            이상치: {formatKRW(savings.breakdown.anomaly)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            항목 {savings.count}건
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ─── Arcus Insights: 부서별 월 비용 ─── */

function DepartmentInsightsCard({
  fetchApi,
  classes,
}: {
  fetchApi: { fetch: typeof fetch };
  classes: ReturnType<typeof useStyles>;
}) {
  const [data, setData] = React.useState<DeptInsights | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetchApi.fetch('/api/gcp-cost/insights/by-department');
        if (!res.ok) throw new Error(await res.text());
        setData(await res.json());
      } catch (e: any) {
        setError(e.message);
      }
    })();
  }, [fetchApi]);

  if (error) {
    return (
      <Alert severity="warning" style={{ marginBottom: 12 }}>
        부서별 비용 조회 실패: {error}
      </Alert>
    );
  }
  if (!data) return null;

  const maxBar = Math.max(
    ...data.departments.map(d => d.rawCost),
    data.platform.rawCost,
    1,
  );

  return (
    <Card variant="outlined" style={{ marginBottom: 12, borderLeft: '4px solid #7b1fa2' }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          style={{ gap: 12, flexWrap: 'wrap' }}
        >
          <Box flex={1} minWidth={260}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              부서별 월 비용 (최근 {data.windowDays}일)
            </Typography>
            <Typography variant="body2" color="textSecondary">
              GKE cost allocation 라벨 기반 — dept × namespace 귀속
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography
              variant="h6"
              style={{ color: '#7b1fa2', fontWeight: 700, lineHeight: 1.1 }}
            >
              {formatKRW(data.grandTotalKRW)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              raw cost 합계
            </Typography>
          </Box>
        </Box>

        <Box mt={2}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader}>부서</TableCell>
                  <TableCell className={classes.tableHeader} align="right">
                    합계
                  </TableCell>
                  <TableCell className={classes.tableHeader} align="right">
                    Prd
                  </TableCell>
                  <TableCell className={classes.tableHeader} align="right">
                    Dev
                  </TableCell>
                  <TableCell className={classes.tableHeader} align="right">
                    Dev 비율
                  </TableCell>
                  <TableCell
                    className={classes.tableHeader}
                    style={{ minWidth: 180 }}
                  >
                    비중
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.departments.map(d => {
                  const devPct = d.rawCost > 0 ? (d.devCost / d.rawCost) * 100 : 0;
                  const barPct = (d.rawCost / maxBar) * 100;
                  return (
                    <TableRow key={d.dept}>
                      <TableCell>
                        <strong>{d.dept}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatKRW(d.rawCost)}</strong>
                      </TableCell>
                      <TableCell align="right">{formatKRW(d.prdCost)}</TableCell>
                      <TableCell align="right">{formatKRW(d.devCost)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${devPct.toFixed(0)}%`}
                          size="small"
                          style={{
                            backgroundColor: devPct > 50 ? '#f57c00' : '#e0e0e0',
                            color: devPct > 50 ? '#fff' : undefined,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box className={classes.costBarBg}>
                          <Box
                            className={classes.costBar}
                            style={{
                              width: `${barPct}%`,
                              backgroundColor: '#7b1fa2',
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* 플랫폼 공통 */}
                <TableRow
                  style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
                >
                  <TableCell>
                    <Tooltip title="dept 라벨 없는 시스템 네임스페이스 + 클러스터 공통 비용 (컨트롤 플레인, 네트워킹 등)">
                      <em>플랫폼 공통</em>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{formatKRW(data.platform.rawCost)}</strong>
                  </TableCell>
                  <TableCell align="right" colSpan={3}>
                    <Typography variant="caption" color="textSecondary">
                      {data.platform.namespaces
                        .slice(0, 3)
                        .map(n => `${n.namespace}(${formatKRW(n.rawCost)})`)
                        .join(' · ')}
                      {data.platform.namespaces.length > 3 &&
                        ` 외 ${data.platform.namespaces.length - 3}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box className={classes.costBarBg}>
                      <Box
                        className={classes.costBar}
                        style={{
                          width: `${(data.platform.rawCost / maxBar) * 100}%`,
                          backgroundColor: '#9e9e9e',
                        }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ─── Arcus Insights: Workload 급증 탐지 ─── */

interface DeployCommit {
  shortId: string;
  title: string;
  author: string;
  committedAt: string;
  webUrl: string;
}
interface DeployHistoryResp {
  entity: string;
  days: number;
  projectFound: boolean;
  projectPath?: string;
  projectWebUrl?: string;
  commits: DeployCommit[];
  hint?: string;
}

function DeployHistoryPanel({
  entity,
  fetchApi,
}: {
  entity: string;
  fetchApi: { fetch: typeof fetch };
}) {
  const [data, setData] = React.useState<DeployHistoryResp | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    (async () => {
      try {
        const res = await fetchApi.fetch(
          `/api/gcp-cost/insights/deploy-history?entity=${encodeURIComponent(entity)}&days=7`,
        );
        if (!res.ok) throw new Error(await res.text());
        setData(await res.json());
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [entity, fetchApi]);

  if (loading) {
    return (
      <Box p={2} display="flex" alignItems="center" style={{ gap: 8 }}>
        <CircularProgress size={16} />
        <Typography variant="caption">배포 이력 조회 중 ({entity})</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box p={2}>
        <Alert severity="warning">배포 이력 조회 실패: {error}</Alert>
      </Box>
    );
  }
  if (!data) return null;

  if (!data.projectFound) {
    return (
      <Box p={2}>
        <Typography variant="caption" color="textSecondary">
          {data.hint || 'GitLab 프로젝트를 찾지 못함'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="caption" color="textSecondary">
          <strong>{data.projectPath}</strong> · 최근 {data.days}일 · {data.commits.length}건
        </Typography>
        {data.projectWebUrl && (
          <a
            href={data.projectWebUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '0.75rem' }}
          >
            GitLab 열기 ↗
          </a>
        )}
      </Box>
      {data.commits.length === 0 ? (
        <Typography variant="caption" color="textSecondary">
          이 기간에 커밋 없음 — 비용 급증 원인이 코드 변경이 아닐 수 있음
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontSize: '0.75rem' }}>Commit</TableCell>
              <TableCell style={{ fontSize: '0.75rem' }}>Title</TableCell>
              <TableCell style={{ fontSize: '0.75rem' }}>Author</TableCell>
              <TableCell style={{ fontSize: '0.75rem' }}>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.commits.map(c => (
              <TableRow key={c.shortId}>
                <TableCell style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  <a href={c.webUrl} target="_blank" rel="noopener noreferrer">
                    {c.shortId}
                  </a>
                </TableCell>
                <TableCell style={{ fontSize: '0.8rem', maxWidth: 480 }}>
                  <Tooltip title={c.title}>
                    <span
                      style={{
                        display: 'inline-block',
                        maxWidth: 480,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {c.title}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ fontSize: '0.75rem' }}>{c.author}</TableCell>
                <TableCell style={{ fontSize: '0.75rem' }}>
                  {new Date(c.committedAt).toLocaleString('ko-KR', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

function WorkloadAnomalyCard({
  fetchApi,
  classes,
}: {
  fetchApi: { fetch: typeof fetch };
  classes: ReturnType<typeof useStyles>;
}) {
  const [data, setData] = React.useState<WorkloadAnomalies | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [expandedEntity, setExpandedEntity] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetchApi.fetch('/api/gcp-cost/insights/workload-anomalies');
        if (!res.ok) throw new Error(await res.text());
        setData(await res.json());
      } catch (e: any) {
        setError(e.message);
      }
    })();
  }, [fetchApi]);

  if (error) {
    return (
      <Alert severity="warning" style={{ marginBottom: 12 }}>
        Workload 이상치 조회 실패: {error}
      </Alert>
    );
  }
  if (!data) return null;

  const accent = data.count > 0 ? '#c2185b' : '#2e7d32';

  return (
    <Card variant="outlined" style={{ marginBottom: 12, borderLeft: `4px solid ${accent}` }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          style={{ gap: 12, flexWrap: 'wrap' }}
        >
          <Box flex={1} minWidth={260}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Arcus Workload 급증 탐지
            </Typography>
            <Typography variant="body2" color="textSecondary">
              최근 {data.windowRecent}일 평균이 {data.windowBaseline}일 기준 대비 +20% 이상 증가한 워크로드 (일 ₩100 초과)
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography
              variant="h6"
              style={{ color: accent, fontWeight: 700, lineHeight: 1.1 }}
            >
              {data.count === 0 ? '정상' : `${data.count}건`}
            </Typography>
            {data.count > 0 && (
              <Typography variant="caption" color="textSecondary">
                월 +{formatKRW(data.totalMonthlyDeltaKRW)} 예상 증가
              </Typography>
            )}
          </Box>
        </Box>

        {data.count === 0 && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 8 }}
          >
            모든 워크로드가 안정 범위 내 (최근 7일 평균이 30일 기준 ±20% 이내)
          </Typography>
        )}

        {data.count > 0 && (
          <Box mt={2}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>Entity</TableCell>
                    <TableCell className={classes.tableHeader}>Workload</TableCell>
                    <TableCell className={classes.tableHeader}>NS</TableCell>
                    <TableCell className={classes.tableHeader} align="right">
                      7일 평균
                    </TableCell>
                    <TableCell className={classes.tableHeader} align="right">
                      기준
                    </TableCell>
                    <TableCell className={classes.tableHeader} align="right">
                      증가율
                    </TableCell>
                    <TableCell className={classes.tableHeader} align="right">
                      월 +추정
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.anomalies.map((a, i) => {
                    const pct = (a.growthRate * 100).toFixed(0);
                    const pctColor =
                      a.growthRate >= 0.5
                        ? '#d32f2f'
                        : a.growthRate >= 0.3
                          ? '#f57c00'
                          : '#9e9e9e';
                    return (
                      <React.Fragment key={i}>
                      <TableRow>
                        <TableCell>
                          {a.entityId ? (
                            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                              <Chip
                                label={a.entityId}
                                size="small"
                                style={{ backgroundColor: '#0097a7', color: '#fff' }}
                              />
                              <IconButton
                                size="small"
                                title="배포 이력 조회"
                                onClick={() =>
                                  setExpandedEntity(
                                    expandedEntity === a.entityId ? null : a.entityId,
                                  )
                                }
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="textSecondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '0.82rem',
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Tooltip title={a.workload}>
                            <span>{a.workload}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell style={{ fontSize: '0.82rem' }}>
                          {a.namespace}
                        </TableCell>
                        <TableCell align="right">
                          {formatKRW(a.recentAvg7d)}/일
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="textSecondary">
                            {formatKRW(a.baselineAvg30d)}/일
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`+${pct}%`}
                            size="small"
                            style={{
                              backgroundColor: pctColor,
                              color: '#fff',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <strong>+{formatKRW(a.monthlyDeltaKRW)}</strong>
                        </TableCell>
                      </TableRow>
                      {expandedEntity === a.entityId && a.entityId && (
                        <TableRow>
                          <TableCell colSpan={7} style={{ padding: 0, backgroundColor: '#f5f5f5' }}>
                            <DeployHistoryPanel entity={a.entityId} fetchApi={fetchApi} />
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginTop: 8, display: 'block' }}
            >
              힌트: 같은 시점 배포 이벤트를 찾으려면 해당 서비스의 Catalog 페이지 → CI/CD 탭에서 최근 커밋 확인
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Arcus Insights: 카탈로그 Entity × 비용 ─── */

function CatalogEntityCard({
  fetchApi,
  classes,
}: {
  fetchApi: { fetch: typeof fetch };
  classes: ReturnType<typeof useStyles>;
}) {
  const [data, setData] = React.useState<CatalogEntityInsights | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [catalogNames, setCatalogNames] = React.useState<Set<string> | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetchApi.fetch('/api/gcp-cost/insights/by-catalog-entity');
        if (!res.ok) throw new Error(await res.text());
        setData(await res.json());
      } catch (e: any) {
        setError(e.message);
      }
    })();
  }, [fetchApi]);

  // 카탈로그 실재 대조 — 비용 윈도(30일)에 남은 디커미션 엔티티 표기용
  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetchApi.fetch(
          '/api/catalog/entities?fields=metadata.name',
        );
        if (!res.ok) return;
        const items: Array<{ metadata: { name: string } }> = await res.json();
        setCatalogNames(new Set(items.map(i => i.metadata.name)));
      } catch {
        /* 대조 실패 = 라벨 생략 (비차단) */
      }
    })();
  }, [fetchApi]);

  if (error) {
    return (
      <Alert severity="warning" style={{ marginBottom: 12 }}>
        카탈로그 비용 조회 실패: {error}
      </Alert>
    );
  }
  if (!data) return null;

  const top = data.entities.slice(0, 10);
  const maxBar = Math.max(...top.map(e => e.rawCost), 1);

  return (
    <Card variant="outlined" style={{ marginBottom: 12, borderLeft: '4px solid #0097a7' }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          style={{ gap: 12, flexWrap: 'wrap' }}
        >
          <Box flex={1} minWidth={260}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              카탈로그 Entity × 비용 TOP
            </Typography>
            <Typography variant="body2" color="textSecondary">
              backstage.io/kubernetes-id 라벨 기반 — Arcus 카탈로그 등록 워크로드만 집계
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography
              variant="h6"
              style={{ color: '#0097a7', fontWeight: 700, lineHeight: 1.1 }}
            >
              {data.count}건
            </Typography>
            <Typography variant="caption" color="textSecondary">
              합계 {formatKRW(data.totalRawCostKRW)} (최근 {data.windowDays}일)
            </Typography>
          </Box>
        </Box>

        <Box mt={2}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader}>Entity</TableCell>
                  <TableCell className={classes.tableHeader}>부서</TableCell>
                  <TableCell className={classes.tableHeader}>환경</TableCell>
                  <TableCell className={classes.tableHeader} align="right">
                    월 비용
                  </TableCell>
                  <TableCell
                    className={classes.tableHeader}
                    style={{ minWidth: 160 }}
                  >
                    비중
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {top.map(e => {
                  const barPct = (e.rawCost / maxBar) * 100;
                  const isOpen = expanded === e.entityId;
                  return (
                    <React.Fragment key={e.entityId}>
                      <TableRow
                        hover
                        onClick={() =>
                          setExpanded(isOpen ? null : e.entityId)
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <strong>{e.entityId}</strong>
                          {catalogNames && !catalogNames.has(e.entityId) && (
                            <Chip
                              label="디커미션됨"
                              size="small"
                              variant="outlined"
                              style={{ marginLeft: 6, color: '#9e9e9e', borderColor: '#9e9e9e' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {e.dept ? (
                            <Chip label={e.dept} size="small" variant="outlined" />
                          ) : (
                            <Typography variant="caption" color="textSecondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {e.environments.map(env => (
                            <Chip
                              key={env}
                              label={env}
                              size="small"
                              style={{
                                marginRight: 4,
                                backgroundColor: env === 'prd' ? '#1976d2' : '#f57c00',
                                color: '#fff',
                              }}
                            />
                          ))}
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatKRW(e.rawCost)}</strong>
                        </TableCell>
                        <TableCell>
                          <Box className={classes.costBarBg}>
                            <Box
                              className={classes.costBar}
                              style={{
                                width: `${barPct}%`,
                                backgroundColor: '#0097a7',
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            style={{ backgroundColor: 'rgba(0,151,167,0.05)' }}
                          >
                            <Box pl={2} pr={2} pt={1} pb={1}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                                gutterBottom
                              >
                                워크로드 상세
                              </Typography>
                              <Table size="small">
                                <TableBody>
                                  {e.workloads.map((w, wi) => (
                                    <TableRow key={wi}>
                                      <TableCell
                                        style={{
                                          fontFamily: 'monospace',
                                          fontSize: '0.82rem',
                                          border: 'none',
                                        }}
                                      >
                                        {w.workload}
                                      </TableCell>
                                      <TableCell
                                        style={{ fontSize: '0.82rem', border: 'none' }}
                                      >
                                        {w.namespace}
                                      </TableCell>
                                      <TableCell
                                        style={{ border: 'none' }}
                                      >
                                        <Chip
                                          label={w.env}
                                          size="small"
                                          style={{
                                            backgroundColor:
                                              w.env === 'prd' ? '#1976d2' : '#f57c00',
                                            color: '#fff',
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        style={{ border: 'none' }}
                                      >
                                        {formatKRW(w.rawCost)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {data.count > 10 && (
            <Typography variant="caption" color="textSecondary">
              외 {data.count - 10}건 더
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

/* ─── Arcus Insights: 정책 위반 탐지 ─── */

function PolicyViolationsCard({
  fetchApi,
  classes,
}: {
  fetchApi: { fetch: typeof fetch };
  classes: ReturnType<typeof useStyles>;
}) {
  const [data, setData] = React.useState<PolicyViolations | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetchApi.fetch('/api/gcp-cost/insights/policy-violations');
        if (!res.ok) throw new Error(await res.text());
        setData(await res.json());
      } catch (e: any) {
        setError(e.message);
      }
    })();
  }, [fetchApi]);

  if (error) {
    return (
      <Alert severity="warning" style={{ marginBottom: 12 }}>
        정책 위반 탐지 실패: {error}
      </Alert>
    );
  }
  if (!data) return null;

  const accent = data.count > 0 ? '#c62828' : '#2e7d32';

  const ruleTitle: Record<PolicyViolation['rule'], string> = {
    'R1-dev-backup': 'Dev 환경 백업 활성',
    'R2-dev-scaledown': 'Dev scaledown/scaleup 비활성',
    'R3-orphan-pvc': '고아 PVC',
  };
  const severityColor = (s: string) =>
    s === 'high' ? '#d32f2f' : s === 'medium' ? '#f57c00' : '#9e9e9e';

  return (
    <Card
      variant="outlined"
      style={{ marginBottom: 12, borderLeft: `4px solid ${accent}` }}
    >
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          style={{ gap: 12, flexWrap: 'wrap' }}
        >
          <Box flex={1} minWidth={260}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              플랫폼 정책 위반 탐지
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Dev 백업 활성 / scaledown 누락 / 고아 PVC 실시간 교차 검사
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography
              variant="h6"
              style={{ color: accent, fontWeight: 700, lineHeight: 1.1 }}
            >
              {data.count === 0 ? '위반 없음' : `${data.count}건 위반`}
            </Typography>
            {data.count > 0 && (
              <Typography variant="caption" color="textSecondary">
                월 ~{formatKRW(data.totalEstimatedMonthlyWasteKRW)} 낭비 추정
              </Typography>
            )}
          </Box>
        </Box>

        {data.count === 0 && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 8 }}
          >
            모든 룰 통과 — Dev 백업 off / scaledown active / 고아 PVC 0건
          </Typography>
        )}

        {data.count > 0 && (
          <Box mt={2}>
            <Box
              display="flex"
              mb={1}
              style={{ gap: 8, flexWrap: 'wrap' }}
            >
              {data.bySeverity.high > 0 && (
                <Chip
                  label={`HIGH ${data.bySeverity.high}`}
                  size="small"
                  style={{ backgroundColor: '#d32f2f', color: '#fff' }}
                />
              )}
              {data.bySeverity.medium > 0 && (
                <Chip
                  label={`MED ${data.bySeverity.medium}`}
                  size="small"
                  style={{ backgroundColor: '#f57c00', color: '#fff' }}
                />
              )}
              {data.bySeverity.low > 0 && (
                <Chip
                  label={`LOW ${data.bySeverity.low}`}
                  size="small"
                  style={{ backgroundColor: '#9e9e9e', color: '#fff' }}
                />
              )}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>룰</TableCell>
                    <TableCell className={classes.tableHeader}>리소스</TableCell>
                    <TableCell className={classes.tableHeader}>클러스터</TableCell>
                    <TableCell className={classes.tableHeader}>상세</TableCell>
                    <TableCell className={classes.tableHeader} align="right">
                      월 낭비
                    </TableCell>
                    <TableCell className={classes.tableHeader} align="center">
                      심각도
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.violations.map((v, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {ruleTitle[v.rule]}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {v.rule}
                        </Typography>
                      </TableCell>
                      <TableCell
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '0.82rem',
                          maxWidth: 220,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Tooltip title={v.resource}>
                          <span>{v.resource}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{v.cluster}</TableCell>
                      <TableCell style={{ maxWidth: 360 }}>
                        <Typography variant="body2">{v.detail}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatKRW(v.estimatedMonthlyWasteKRW)}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={v.severity.toUpperCase()}
                          size="small"
                          style={{
                            backgroundColor: severityColor(v.severity),
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Arcus Insights: LB Forwarding Rule 통합 후보 ─── */

function LbConsolidationCard({
  fetchApi,
}: {
  fetchApi: { fetch: typeof fetch };
}) {
  const [data, setData] = React.useState<LbConsolidation | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetchApi.fetch('/api/gcp-cost/insights/lb-consolidation');
        if (!res.ok) throw new Error(await res.text());
        setData(await res.json());
      } catch (e: any) {
        setError(e.message);
      }
    })();
  }, [fetchApi]);

  if (error) {
    return (
      <Alert severity="warning" style={{ marginBottom: 12 }}>
        LB 통합 분석 실패: {error}
      </Alert>
    );
  }
  if (!data) return null;

  const savingsPct =
    data.totalRawCostKRW > 0
      ? (data.estimatedMonthlySavingsKRW / data.totalRawCostKRW) * 100
      : 0;

  return (
    <Card
      variant="outlined"
      style={{ marginBottom: 12, borderLeft: '4px solid #00796b' }}
    >
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          style={{ gap: 12, flexWrap: 'wrap' }}
        >
          <Box flex={1} minWidth={260}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              LB Forwarding Rule 통합 후보
            </Typography>
            <Typography variant="body2" color="textSecondary">
              현재 {data.rulesTotal}개 (k8s Service {data.rulesK8s} + 시스템 {data.rulesSystem}){' '}
              · host-based routing 통합 시 {data.targetRules}개로 축소 가능
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography
              variant="h6"
              style={{ color: '#00796b', fontWeight: 700, lineHeight: 1.1 }}
            >
              -{formatKRW(data.estimatedMonthlySavingsKRW)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              월 예상 절감 (현재 비용의 {savingsPct.toFixed(0)}%)
            </Typography>
          </Box>
        </Box>

        <Box
          mt={2}
          display="flex"
          style={{ gap: 16, flexWrap: 'wrap' }}
        >
          <Box flex="1 1 200px">
            <Typography variant="caption" color="textSecondary">
              현재 월 비용
            </Typography>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {formatKRW(data.totalRawCostKRW)}
            </Typography>
          </Box>
          <Box flex="1 1 200px">
            <Typography variant="caption" color="textSecondary">
              축소 가능 rules
            </Typography>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {data.reducibleRules}개
            </Typography>
          </Box>
          <Box flex="1 1 200px">
            <Typography variant="caption" color="textSecondary">
              SKU 분포
            </Typography>
            <Box>
              {data.skuBreakdown.slice(0, 3).map((s, i) => (
                <Typography
                  key={i}
                  variant="caption"
                  display="block"
                  color="textSecondary"
                  style={{
                    maxWidth: 320,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Tooltip title={s.sku}>
                    <span>
                      {s.sku.replace('Cloud Load Balancer ', '')}: {formatKRW(s.rawCost)}
                    </span>
                  </Tooltip>
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          mt={2}
          p={1.5}
          style={{
            backgroundColor: 'rgba(0,121,107,0.06)',
            borderRadius: 4,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {data.note}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ─── Recommendations Tab ─── */

function RecommendationsTab({
  fetchApi,
  classes,
}: {
  fetchApi: { fetch: typeof fetch };
  classes: ReturnType<typeof useStyles>;
}) {
  const [recs, setRecs] = React.useState<Recommendation[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchApi.fetch('/api/gcp-cost/recommendations');
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRecs(data.recommendations || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  React.useEffect(() => {
    load();
  }, [load]);

  // ── 카테고리 분류 ──
  type SectionKey = 'commitment' | 'machine_type' | 'idle' | 'anomaly' | 'other';
  const classify = (r: Recommendation): SectionKey => {
    if (r.source === 'anomaly') return 'anomaly';
    const c = (r.category || '').toUpperCase();
    if (c.includes('COMMIT')) return 'commitment';
    if (c.includes('MACHINE_TYPE') || c.includes('RIGHTSIZING')) return 'machine_type';
    if (c.includes('IDLE') || c.includes('STOP') || c.includes('DELETE') || c.includes('SNAPSHOT')) return 'idle';
    return 'other';
  };

  const SECTIONS: Array<{
    key: SectionKey;
    title: string;
    hint: string;
    accent: string;
  }> = [
    {
      key: 'commitment',
      title: '커밋트먼트 할인 (CUD)',
      hint: '지속적으로 사용 중인 리소스를 1~3년 약정으로 할인받는 옵션',
      accent: '#1976d2',
    },
    {
      key: 'machine_type',
      title: '머신 타입 최적화',
      hint: '현재 사용량에 비해 과다/부적합한 VM 크기를 재조정',
      accent: '#388e3c',
    },
    {
      key: 'idle',
      title: '유휴 리소스 정리',
      hint: '최근 사용 이력이 없는 디스크/스냅샷/VM 삭제',
      accent: '#f57c00',
    },
    {
      key: 'anomaly',
      title: '비용 급증 탐지',
      hint: '최근 7일 평균이 30일 기준 대비 +30% 이상 증가한 서비스',
      accent: '#d32f2f',
    },
    {
      key: 'other',
      title: '기타 추천',
      hint: '분류되지 않은 기타 Recommender 결과',
      accent: '#757575',
    },
  ];

  const bySection = React.useMemo(() => {
    const map = new Map<SectionKey, Recommendation[]>();
    for (const r of recs) {
      const k = classify(r);
      const arr = map.get(k) || [];
      arr.push(r);
      map.set(k, arr);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recs]);

  const totalSavings = recs.reduce((s, r) => s + r.savingsKRW, 0);
  const topFive = [...recs].sort((a, b) => b.savingsKRW - a.savingsKRW).slice(0, 5);

  // 리소스 경로에서 마지막 세그먼트만 추출
  const shortResource = (path: string): string => {
    if (!path) return '-';
    const parts = path.split('/');
    return parts[parts.length - 1] || path;
  };

  return (
    <>
      {/* 섹션 1: Arcus 인사이트 (Arcus만 아는 컨텍스트) */}
      <Typography
        variant="overline"
        color="textSecondary"
        style={{ display: 'block', marginBottom: 4, letterSpacing: 1 }}
      >
        ARCUS 인사이트
      </Typography>
      <DepartmentInsightsCard fetchApi={fetchApi} classes={classes} />
      <CatalogEntityCard fetchApi={fetchApi} classes={classes} />
      <WorkloadAnomalyCard fetchApi={fetchApi} classes={classes} />
      <LbConsolidationCard fetchApi={fetchApi} />
      <PolicyViolationsCard fetchApi={fetchApi} classes={classes} />

      {/* 섹션 2: GCP Recommender 기반 절감 가능 항목 */}
      <Typography
        variant="overline"
        color="textSecondary"
        style={{ display: 'block', marginBottom: 4, marginTop: 16, letterSpacing: 1 }}
      >
        GCP 공식 추천 · 절감 가능 항목
      </Typography>

      {/* 헤드라인 */}
      <Box mb={2}>
        <Typography variant="h5" style={{ fontWeight: 600 }}>
          이번 달 <span style={{ color: '#d32f2f' }}>{formatKRW(totalSavings)}</span> 절감 가능
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Recommender API + BigQuery 이상치 분석 결과 ({recs.length}건, 1시간 캐시)
        </Typography>
        {loading && <CircularProgress size={20} style={{ marginTop: 8 }} />}
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {!loading && recs.length === 0 && !error && (
        <Alert severity="info">
          현재 추천 항목이 없습니다. Recommender API가 리소스를 분석하려면 최초 몇 시간이 필요할 수 있습니다.
        </Alert>
      )}

      {/* 카테고리 섹션들 */}
      {SECTIONS.map(sec => {
        const items = bySection.get(sec.key) || [];
        if (items.length === 0) return null;
        const secTotal = items.reduce((s, r) => s + r.savingsKRW, 0);
        const secPct = totalSavings > 0 ? (secTotal / totalSavings) * 100 : 0;

        // 리소스별로 묶기 (CUD처럼 같은 SKU 여러 건 집계)
        const preview = items.slice(0, 3);
        const remaining = items.length - preview.length;

        return (
          <Card
            key={sec.key}
            variant="outlined"
            style={{ marginBottom: 12, borderLeft: `4px solid ${sec.accent}` }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                style={{ gap: 12, flexWrap: 'wrap' }}
              >
                <Box flex={1} minWidth={260}>
                  <Typography variant="h6" style={{ fontWeight: 600 }}>
                    {sec.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {sec.hint}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography
                    variant="h6"
                    style={{ color: sec.accent, fontWeight: 700, lineHeight: 1.1 }}
                  >
                    {formatKRW(secTotal)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {items.length}건 · 전체의 {secPct.toFixed(0)}%
                  </Typography>
                </Box>
              </Box>

              <Box mt={1.5} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {preview.map((r, i) => (
                  <Box
                    key={i}
                    display="flex"
                    alignItems="center"
                    style={{ gap: 12 }}
                  >
                    <Typography
                      variant="body2"
                      style={{
                        fontFamily: 'monospace',
                        minWidth: 180,
                        maxWidth: 260,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: '#424242',
                      }}
                    >
                      <Tooltip title={r.resource}>
                        <span>{shortResource(r.resource)}</span>
                      </Tooltip>
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ flex: 1, color: '#616161' }}
                    >
                      {r.action}
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 600, minWidth: 80, textAlign: 'right' }}
                    >
                      {formatKRW(r.savingsKRW)}
                    </Typography>
                  </Box>
                ))}
                {remaining > 0 && (
                  <Typography variant="caption" color="textSecondary">
                    외 {remaining}건 더
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        );
      })}

      {/* Top 5 압축 리스트 */}
      {recs.length > 0 && (
        <Card variant="outlined" style={{ marginTop: 16 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Top 5 · 가장 큰 절감 항목
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>#</TableCell>
                    <TableCell className={classes.tableHeader}>카테고리</TableCell>
                    <TableCell className={classes.tableHeader}>리소스</TableCell>
                    <TableCell className={classes.tableHeader}>권장 조치</TableCell>
                    <TableCell className={classes.tableHeader} align="right">
                      월 절감
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topFive.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <Chip
                          label={SECTIONS.find(s => s.key === classify(r))?.title || r.category}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          maxWidth: 260,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Tooltip title={r.resource}>
                          <span>{shortResource(r.resource)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell style={{ maxWidth: 380 }}>
                        <Typography variant="body2">{r.action}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatKRW(r.savingsKRW)}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </>
  );
}

/* ─── Overview Tab ─── */

function OverviewTab({
  monthly,
  savings,
  classes,
  onSavingsClick,
}: {
  monthly: MonthlySummary[];
  savings: SavingsPotential | null;
  classes: ReturnType<typeof useStyles>;
  onSavingsClick?: () => void;
}) {
  const sorted = [...monthly].sort((a, b) => b.month.localeCompare(a.month));
  const current = sorted[0];
  const previous = sorted[1];
  const change = current && previous ? pctChange(current.netCost, previous.netCost) : null;

  const chartData = [...monthly]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map(m => ({
      month: monthLabel(m.month),
      원가: Math.round(m.totalCost),
      할인: Math.round(Math.abs(m.totalCredit)),
      실청구: Math.round(m.netCost),
    }));

  return (
    <>
      {/* Savings Potential Card (상단) */}
      {savings && (
        <Box mb={2}>
          <SavingsPotentialCard savings={savings} onClick={onSavingsClick} />
        </Box>
      )}

      {/* Stats Cards */}
      <Box className={classes.statsRow}>
        <Card variant="outlined" className={classes.statCard}>
          <CardContent>
            <Typography className={classes.statValue}>
              {current ? formatKRW(current.netCost) : '-'}
            </Typography>
            <Typography className={classes.statLabel}>이번달 실청구</Typography>
            {change !== null && (
              <Box
                className={`${classes.statChange} ${change > 0 ? classes.positive : classes.negative}`}
              >
                {change > 0 ? (
                  <TrendingUpIcon fontSize="small" />
                ) : (
                  <TrendingDownIcon fontSize="small" />
                )}
                {Math.abs(change).toFixed(1)}% vs 전월
              </Box>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" className={classes.statCard}>
          <CardContent>
            <Typography className={classes.statValue}>
              {current ? formatKRW(current.totalCost) : '-'}
            </Typography>
            <Typography className={classes.statLabel}>이번달 원가</Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" className={classes.statCard}>
          <CardContent>
            <Typography className={classes.statValue}>
              {current ? formatKRW(Math.abs(current.totalCredit)) : '-'}
            </Typography>
            <Typography className={classes.statLabel}>이번달 할인</Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" className={classes.statCard}>
          <CardContent>
            <Typography className={classes.statValue}>
              {current ? formatKRW(current.invoiceTotal) : '-'}
            </Typography>
            <Typography className={classes.statLabel}>
              인보이스 (세금 포함)
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Monthly Bar Chart */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            월별 비용 추이
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', marginBottom: 8 }}
          >
            청구월(invoice month) 기준 — 일별 추이 탭(사용일 KST 기준)과 집계
            기준이 달라 합계가 다를 수 있음
          </Typography>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis
                tickFormatter={(v: number) => formatKRW(v)}
              />
              <RechartsTooltip
                formatter={(value: number, name: string) => [
                  `₩${value.toLocaleString()}`,
                  name,
                ]}
              />
              <Legend />
              <Bar dataKey="원가" fill="#1976d2" stackId="a" />
              <Bar dataKey="할인" fill="#f57c00" stackId="b" />
              <Bar dataKey="실청구" fill="#388e3c" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Monthly Table */}
      <Box mt={2}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              월별 상세
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>월</TableCell>
                    <TableCell className={classes.tableHeader} align="right">
                      원가
                    </TableCell>
                    <TableCell className={classes.tableHeader} align="right">
                      할인
                    </TableCell>
                    <TableCell className={classes.tableHeader} align="right">
                      실청구
                    </TableCell>
                    <TableCell className={classes.tableHeader} align="right">
                      세금
                    </TableCell>
                    <TableCell className={classes.tableHeader} align="right">
                      인보이스
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map(m => (
                    <TableRow key={m.month}>
                      <TableCell>{formatMonth(m.month)}</TableCell>
                      <TableCell align="right">
                        ₩{Math.round(m.totalCost).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        ₩{Math.round(Math.abs(m.totalCredit)).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          ₩{Math.round(m.netCost).toLocaleString()}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        ₩{Math.round(m.tax).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        ₩{Math.round(m.invoiceTotal).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}

/* ─── Service Breakdown Tab ─── */

function ServiceTab({
  fetchApi,
  classes,
  months,
}: {
  fetchApi: { fetch: typeof fetch };
  classes: ReturnType<typeof useStyles>;
  months: string[];
}) {
  const [selectedMonth, setSelectedMonth] = React.useState(months[0] || '');
  const [services, setServices] = React.useState<ServiceCost[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchServices = React.useCallback(
    async (month: string) => {
      if (!month) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetchApi.fetch(
          `/api/gcp-cost/services?month=${month}`,
        );
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text);
        }
        const data = await res.json();
        setServices(data.services || []);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [fetchApi],
  );

  React.useEffect(() => {
    if (selectedMonth) fetchServices(selectedMonth);
  }, [selectedMonth, fetchServices]);

  const maxCost = Math.max(...services.map(s => s.cost), 1);
  const totalNet = services.reduce((acc, s) => acc + s.netCost, 0);

  const pieData = services
    .filter(s => s.netCost > 0)
    .slice(0, 10)
    .map(s => ({ name: s.serviceName, value: Math.round(s.netCost) }));

  return (
    <>
      <Box className={classes.filterRow}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel>월 선택</InputLabel>
          <Select
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value as string)}
            label="월 선택"
          >
            {months.map(m => (
              <MenuItem key={m} value={m}>
                {formatMonth(m)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loading && <CircularProgress size={20} />}
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {!loading && services.length > 0 && (
        <>
          {/* Pie Chart */}
          <Card variant="outlined" style={{ marginBottom: 16 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                서비스별 비용 비율 ({formatMonth(selectedMonth)})
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                    dataKey="value"
                  >
                    {pieData.map((_entry, idx) => (
                      <Cell
                        key={`cell-${idx}`}
                        fill={COLORS[idx % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value: number) => [
                      `₩${value.toLocaleString()}`,
                      '비용',
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Service Table */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                서비스별 상세 ({services.length}개)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeader}>
                        서비스
                      </TableCell>
                      <TableCell className={classes.tableHeader} align="right">
                        원가
                      </TableCell>
                      <TableCell
                        className={classes.tableHeader}
                        align="right"
                      >
                        할인
                      </TableCell>
                      <TableCell className={classes.tableHeader} align="right">
                        실청구
                      </TableCell>
                      <TableCell className={classes.tableHeader} align="right">
                        비율
                      </TableCell>
                      <TableCell className={classes.tableHeader} width="20%">
                        &nbsp;
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {services.map((s, idx) => (
                      <TableRow key={s.serviceId}>
                        <TableCell>
                          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <Box
                              width={12}
                              height={12}
                              borderRadius="50%"
                              bgcolor={COLORS[idx % COLORS.length]}
                              flexShrink={0}
                            />
                            {s.serviceName}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          ₩{Math.round(s.cost).toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          ₩{Math.round(Math.abs(s.credit)).toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            ₩{Math.round(s.netCost).toLocaleString()}
                          </strong>
                        </TableCell>
                        <TableCell align="right">
                          {totalNet > 0
                            ? `${((s.netCost / totalNet) * 100).toFixed(1)}%`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Box className={classes.costBarBg}>
                            <Box
                              className={classes.costBar}
                              style={{
                                width: `${(s.cost / maxCost) * 100}%`,
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}

/* ─── Daily Trend Tab ─── */

function DailyTab({
  fetchApi,
  classes,
}: {
  fetchApi: { fetch: typeof fetch };
  classes: ReturnType<typeof useStyles>;
}) {
  const [range, setRange] = React.useState('30');
  const [daily, setDaily] = React.useState<DailyCost[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchDaily = React.useCallback(
    async (days: number) => {
      setLoading(true);
      setError(null);
      try {
        // 구간을 사용일(KST) 기준으로 계산 — UTC 오늘이면 KST 00~09시에 당일 누락
        const DAY_MS = 24 * 60 * 60 * 1000;
        const kstNowMs = Date.now() + 9 * 60 * 60 * 1000;
        const to = new Date(kstNowMs).toISOString().split('T')[0];
        const from = new Date(kstNowMs - days * DAY_MS)
          .toISOString()
          .split('T')[0];
        const res = await fetchApi.fetch(
          `/api/gcp-cost/daily?from=${from}&to=${to}`,
        );
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text);
        }
        const data = await res.json();
        setDaily(data.daily || []);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [fetchApi],
  );

  React.useEffect(() => {
    fetchDaily(parseInt(range, 10));
  }, [range, fetchDaily]);

  const avgCost =
    daily.length > 0
      ? daily.reduce((acc, d) => acc + d.netCost, 0) / daily.length
      : 0;
  const maxDay = daily.reduce(
    (max, d) => (d.netCost > (max?.netCost || 0) ? d : max),
    daily[0],
  );
  const minDay = daily.reduce(
    (min, d) => (d.netCost < (min?.netCost || Infinity) ? d : min),
    daily[0],
  );

  return (
    <>
      <Box className={classes.filterRow}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>기간</InputLabel>
          <Select
            value={range}
            onChange={e => setRange(e.target.value as string)}
            label="기간"
          >
            <MenuItem value="7">최근 7일</MenuItem>
            <MenuItem value="30">최근 30일</MenuItem>
            <MenuItem value="90">최근 90일</MenuItem>
          </Select>
        </FormControl>
        {loading && <CircularProgress size={20} />}
      </Box>

      <Typography
        variant="caption"
        color="textSecondary"
        style={{ display: 'block', marginBottom: 12 }}
      >
        사용일(KST) 기준 — 개요(청구월 기준)와 집계 기준이 달라 합계가 다를 수
        있음
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {!loading && daily.length > 0 && (
        <>
          <Box className={classes.statsRow}>
            <Card variant="outlined" className={classes.statCard}>
              <CardContent>
                <Typography className={classes.statValue}>
                  {formatKRW(avgCost)}
                </Typography>
                <Typography className={classes.statLabel}>일평균</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" className={classes.statCard}>
              <CardContent>
                <Typography className={classes.statValue}>
                  {maxDay ? formatKRW(maxDay.netCost) : '-'}
                </Typography>
                <Typography className={classes.statLabel}>
                  최고 ({maxDay?.date?.slice(5) || '-'})
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" className={classes.statCard}>
              <CardContent>
                <Typography className={classes.statValue}>
                  {minDay ? formatKRW(minDay.netCost) : '-'}
                </Typography>
                <Typography className={classes.statLabel}>
                  최저 ({minDay?.date?.slice(5) || '-'})
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                일별 비용 추이
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d: string) => d.slice(5)}
                  />
                  <YAxis tickFormatter={(v: number) => formatKRW(v)} />
                  <RechartsTooltip
                    formatter={(value: number, name: string) => [
                      `₩${Math.round(value).toLocaleString()}`,
                      name === 'netCost'
                        ? '실청구'
                        : name === 'totalCost'
                          ? '원가'
                          : name,
                    ]}
                    labelFormatter={(label: string) => label}
                  />
                  <Legend
                    formatter={(value: string) =>
                      value === 'netCost'
                        ? '실청구'
                        : value === 'totalCost'
                          ? '원가'
                          : value
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="totalCost"
                    stroke="#1976d2"
                    strokeWidth={1}
                    dot={false}
                    strokeDasharray="5 5"
                  />
                  <Line
                    type="monotone"
                    dataKey="netCost"
                    stroke="#388e3c"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}

/* ─── Savings Tab (절감/약정) ─── */

const CREDIT_TYPE_LABELS: Record<string, string> = {
  FEE_UTILIZATION_OFFSET: '약정 상쇄 (Flexible CUD)',
  RESELLER_MARGIN: '리셀러 마진',
  DISCOUNT: '할인',
  PROMOTION: '프로모션',
};

function creditTypeLabel(type: string): string {
  return CREDIT_TYPE_LABELS[type] || type;
}

function formatMonth(m: string | null): string {
  if (!m || m.length !== 6) return m || '-';
  return `${m.slice(0, 4)}-${m.slice(4)}`;
}

function SavingsTab({
  fetchApi,
  classes,
}: {
  fetchApi: { fetch: typeof fetch };
  classes: ReturnType<typeof useStyles>;
}) {
  const [range, setRange] = React.useState('6');
  const [data, setData] = React.useState<SavingsSummary | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchSavings = React.useCallback(
    async (months: string) => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchApi.fetch(
          `/api/gcp-cost/savings-summary?months=${months}`,
        );
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text);
        }
        setData(await res.json());
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [fetchApi],
  );

  React.useEffect(() => {
    fetchSavings(range);
  }, [range, fetchSavings]);

  const monthly = data?.monthly || [];
  // 활용률 표시 기준 = 약정 수수료가 있는 최신 월 (당월 = 진행 중 부분집계)
  const latestCommit = monthly.find(m => m.commitment.fee > 0);
  const utilization = latestCommit?.commitment.utilizationPct ?? null;

  // 스택 차트 데이터 (오름차순, 타입별 절감액 = 양수 변환)
  const creditTypes = Array.from(
    new Set(monthly.flatMap(m => Object.keys(m.byType))),
  ).sort();
  const chartData = [...monthly].reverse().map(m => {
    const row: Record<string, string | number> = {
      month: formatMonth(m.month),
      절감률: Math.round(m.savingsPct * 10) / 10,
    };
    creditTypes.forEach(t => {
      row[creditTypeLabel(t)] = Math.round(-(m.byType[t] || 0));
    });
    return row;
  });

  return (
    <>
      <Box className={classes.filterRow}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>기간</InputLabel>
          <Select
            value={range}
            onChange={e => setRange(e.target.value as string)}
            label="기간"
          >
            <MenuItem value="3">최근 3개월</MenuItem>
            <MenuItem value="6">최근 6개월</MenuItem>
            <MenuItem value="12">최근 12개월</MenuItem>
          </Select>
        </FormControl>
        {loading && <CircularProgress size={20} />}
      </Box>

      <Typography
        variant="caption"
        color="textSecondary"
        style={{ display: 'block', marginBottom: 12 }}
      >
        청구월(invoice month) 기준 — BigQuery Billing Export credits 원본 집계
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {!loading && data && monthly.length > 0 && (
        <>
          {/* ① 약정 현황 */}
          <Card variant="outlined" style={{ marginBottom: 16 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                약정 현황
              </Typography>
              {data.commitment.sku ? (
                <>
                  <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                    <Chip
                      label={data.commitment.sku}
                      color="primary"
                      size="small"
                    />
                    <Typography variant="body2" color="textSecondary">
                      개시 {formatMonth(data.commitment.since)}
                    </Typography>
                  </Box>
                  {latestCommit && utilization !== null && (
                    <Box mt={2}>
                      <Typography variant="body2" gutterBottom>
                        활용률 {utilization.toFixed(1)}% (
                        {formatMonth(latestCommit.month)} — 수수료{' '}
                        {formatKRW(latestCommit.commitment.fee)} / 상쇄{' '}
                        {formatKRW(-latestCommit.commitment.feeOffset)})
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(utilization, 100)}
                        style={{
                          height: 10,
                          borderRadius: 5,
                        }}
                        color={utilization < 90 ? 'secondary' : 'primary'}
                      />
                      {utilization < 90 && (
                        <Typography
                          variant="caption"
                          color="error"
                          style={{ display: 'block', marginTop: 4 }}
                        >
                          활용률 90% 미만 — 약정 대비 사용량 부족 (수수료 낭비
                          구간)
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  조회 기간 내 약정(Committed Use Discounts) 수수료 내역이
                  없습니다.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* ② 월별 절감 스택 차트 + 절감률 */}
          <Card variant="outlined" style={{ marginBottom: 16 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                월별 절감액 (credit 타입별)
              </Typography>
              <ResponsiveContainer width="100%" height={360}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis
                    yAxisId="krw"
                    tickFormatter={(v: number) => formatKRW(v)}
                  />
                  <YAxis
                    yAxisId="pct"
                    orientation="right"
                    unit="%"
                    domain={[0, 100]}
                  />
                  <RechartsTooltip
                    formatter={(value: number, name: string) =>
                      name === '절감률'
                        ? [`${value}%`, name]
                        : [`₩${Math.round(value).toLocaleString()}`, name]
                    }
                  />
                  <Legend />
                  {creditTypes.map((t, idx) => (
                    <Bar
                      key={t}
                      yAxisId="krw"
                      dataKey={creditTypeLabel(t)}
                      stackId="credits"
                      fill={COLORS[idx % COLORS.length]}
                    />
                  ))}
                  <Line
                    yAxisId="pct"
                    type="monotone"
                    dataKey="절감률"
                    stroke="#d32f2f"
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* ③ 커버리지 참고치 */}
          {latestCommit && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  약정 커버리지 참고치 ({formatMonth(latestCommit.month)})
                </Typography>
                <Box className={classes.statsRow}>
                  <Card variant="outlined" className={classes.statCard}>
                    <CardContent>
                      <Typography className={classes.statValue}>
                        {formatKRW(latestCommit.eligibleProxyGross)}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        CUD 대상 후보 사용량 (proxy)
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" className={classes.statCard}>
                    <CardContent>
                      <Typography className={classes.statValue}>
                        {formatKRW(-latestCommit.commitment.feeOffset)}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        약정 상쇄액
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" className={classes.statCard}>
                    <CardContent>
                      <Typography className={classes.statValue}>
                        {latestCommit.eligibleProxyGross > 0
                          ? `${((-latestCommit.commitment.feeOffset / latestCommit.eligibleProxyGross) * 100).toFixed(0)}%`
                          : '-'}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        커버리지 (참고)
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: 'block', marginTop: 8 }}
                >
                  ★ 참고치 — 대상 후보 = Autopilot pod + GCE vCPU/RAM SKU
                  근사치. 리셀러 계약 조건 비공개로 정밀 eligibility 산출 불가,
                  의사결정 참고용으로만 사용.
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </>
  );
}

/* ─── Main Page ─── */

export const GcpCostPage = () => {
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);
  const classes = useStyles();

  const [activeTab, setActiveTab] = React.useState(0);
  const [status, setStatus] = React.useState<CostStatus | null>(null);
  const [monthly, setMonthly] = React.useState<MonthlySummary[]>([]);
  const [savings, setSavings] = React.useState<SavingsPotential | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const isPrd = !(
    configApi.getOptionalString('app.baseUrl') ?? ''
  ).includes('-dev');

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. 설정 상태 확인
      const statusRes = await fetchApi.fetch('/api/gcp-cost/status');
      if (!statusRes.ok) throw new Error(`Status API: ${statusRes.status}`);
      const st: CostStatus = await statusRes.json();
      setStatus(st);

      if (!st.bigqueryConfigured) {
        setLoading(false);
        return;
      }

      // 2. 월별 비용 (최근 6개월)
      const monthlyRes = await fetchApi.fetch('/api/gcp-cost/monthly?months=6');
      if (!monthlyRes.ok) {
        const text = await monthlyRes.text();
        throw new Error(text);
      }
      const monthlyData: MonthlySummary[] = await monthlyRes.json();
      setMonthly(monthlyData);

      // 3. Savings Potential (실패해도 치명적이지 않음 → 로그만)
      try {
        const sp = await fetchApi.fetch('/api/gcp-cost/savings-potential');
        if (sp.ok) {
          setSavings((await sp.json()) as SavingsPotential);
        }
      } catch {
        // ignore — 카드만 숨김
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const months = [...monthly]
    .sort((a, b) => b.month.localeCompare(a.month))
    .map(m => m.month);

  if (loading) {
    return (
      <Page themeId="tool">
        <Header title="GCP Cost" subtitle="Cloud 비용 분석">
          <HeaderLabel label="Env" value={isPrd ? 'Production' : 'Development'} />
        </Header>
        <Content>
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header title="GCP Cost" subtitle="Cloud 비용 분석">
        <HeaderLabel
          label="Env"
          value={isPrd ? 'Production' : 'Development'}
        />
        <HeaderLabel
          label="Project"
          value={status?.projectId || '-'}
        />
      </Header>
      <Content>
        {error && (
          <Alert
            severity="error"
            style={{ marginBottom: 16 }}
            action={
              <Tooltip title="새로고침">
                <IconButton size="small" onClick={fetchData}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            }
          >
            {error}
          </Alert>
        )}

        {status && !status.bigqueryConfigured ? (
          <SetupGuide status={status} classes={classes} />
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Tabs
                value={activeTab}
                onChange={(_e, v) => setActiveTab(v)}
                indicatorColor="primary"
              >
                <Tab label="개요" />
                <Tab label="서비스별 상세" />
                <Tab label="일별 추이" />
                <Tab label="추천" />
                <Tab label="절감/약정" />
              </Tabs>
              <Tooltip title="새로고침">
                <IconButton size="small" onClick={fetchData}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {activeTab === 0 && (
              monthly.length === 0 ? (
                <Alert severity="info" style={{ marginTop: 16 }}>
                  BigQuery Billing Export 데이터 축적 중입니다. 활성화 후 1~2일이 소요됩니다.
                  데이터가 준비되면 자동으로 비용 차트가 표시됩니다.
                </Alert>
              ) : (
                <OverviewTab
                  monthly={monthly}
                  savings={savings}
                  classes={classes}
                  onSavingsClick={() => setActiveTab(3)}
                />
              )
            )}

            {activeTab === 1 && (
              <ServiceTab
                fetchApi={fetchApi}
                classes={classes}
                months={months}
              />
            )}

            {activeTab === 2 && (
              <DailyTab fetchApi={fetchApi} classes={classes} />
            )}

            {activeTab === 3 && (
              <RecommendationsTab fetchApi={fetchApi} classes={classes} />
            )}

            {activeTab === 4 && (
              <SavingsTab fetchApi={fetchApi} classes={classes} />
            )}
          </>
        )}
      </Content>
    </Page>
  );
};
